<template>
  <div class="mobile-driver-wrapper page-wrapper">
    <!-- LOADING -->
    <div v-if="isLoading" class="loading-screen">
      <div class="spinner"></div>
      <p>Đang tải dữ liệu...</p>
    </div>

    <!-- ERROR -->
    <div v-else-if="step === STEPS.ERROR" class="error-screen">
      <i class="fa fa-exclamation-triangle"></i>
      <h3>Đã xảy ra lỗi</h3>
      <p>{{ error || "Không thể tải dữ liệu" }}</p>
      <button class="btn-primary-large" @click="initializeDashboard">
        Thử lại
      </button>
    </div>

    <!-- NO ASSIGNMENT -->
    <div v-else-if="step === STEPS.NO_ASSIGNMENT" class="empty-screen">
      <i class="fa fa-info-circle"></i>
      <h3>Chưa có xe phân công</h3>
      <p>Vui lòng liên hệ điều phối để nhận xe.</p>
      <button class="btn-primary-large" style="max-width:260px" @click="initializeDashboard">
        Làm mới
      </button>
    </div>

    <!-- CONTENT -->
    <div v-else class="main-content">
      <!-- Vehicle card (nhỏ gọn giống UI mới) -->
      <div class="vehicle-card" v-if="xeHienTai">
        <div class="vehicle-header">
          <div class="vh-left">
            <i :class="xeIcon"></i>
            <strong>{{ xeHienTai.bienSo }}</strong>
          </div>
          <div v-if="currentOdo ?? xeHienTai.odoGanNhat" class="odo-badge">
            <i class="fa fa-tachometer-alt"></i>
            {{
              (currentOdo ?? xeHienTai.odoGanNhat)
                ? (currentOdo ?? xeHienTai.odoGanNhat).toLocaleString()
                : "---"
            }} km
          </div>
        </div>

        <div class="vehicle-info">
          {{ xeHienTai.hangXe || "-" }}{{ xeHienTai.model ? " " + xeHienTai.model : "" }}
        </div>

        <div class="pill-row">
          <span class="pill">{{ xeHienTai.tenLoaiXe || "Xe" }}</span>
          <span class="pill">{{ xeHienTai.loaiNhienLieu || "Nhiên liệu ?" }}</span>
          <span class="pill" v-if="xeHienTai.namSx">NSX {{ xeHienTai.namSx }}</span>
        </div>
      </div>

      <!-- Driver mini card -->
      <div class="driver-mini" v-if="taiXeHienTai">
        <div class="d-left">
          <img
            class="avatar"
            :src="taiXeHienTai.avatarUrl || '/assets/img/avatar-placeholder.png'"
            alt="Avatar"
          />
          <div class="d-text">
            <div class="name">{{ taiXeHienTai.hoTen || "Tài xế" }}</div>
            <div class="sub">
              <span class="pill">{{ taiXeHienTai.chucVu || "Tài xế" }}</span>
              <span class="pill">{{ taiXeHienTai.phongBan || "Phòng ban ?" }}</span>
            </div>
          </div>
        </div>

        <div class="d-right">
          <a
            v-if="taiXeHienTai.soDienThoai"
            class="quick"
            :href="`tel:${taiXeHienTai.soDienThoai}`"
          >
            <i class="fa fa-phone"></i>
          </a>
          <a
            v-if="taiXeHienTai.email"
            class="quick"
            :href="`mailto:${taiXeHienTai.email}`"
          >
            <i class="fa fa-envelope"></i>
          </a>
        </div>
      </div>

      <!-- ===== HOME TAB ===== -->
      <div v-if="activeBottomTab === 'HOME'">
        <!-- ASSIGNED -->
        <div v-if="step === STEPS.ASSIGNED" class="step-container">
          <div class="step-header">
            <div class="step-number">1</div>
            <h2>Bắt đầu ca làm việc</h2>
          </div>
          <p class="step-desc">Nhấn nút bên dưới để xác nhận bắt đầu ca và nhận xe</p>
          <button class="btn-primary-large" @click="handleNhanXe" :disabled="isLoading">
            <i class="fa fa-key"></i> Bắt đầu ca & Nhận xe
          </button>
        </div>

        <!-- CHECKLIST -->
        <div v-else-if="step === STEPS.CHECKLIST" class="step-container warning">
          <div class="step-header">
            <div class="step-number">2</div>
            <h2>Kiểm tra an toàn</h2>
          </div>
          <div class="warning-box">
            <i class="fa fa-exclamation-triangle"></i>
            <p>Bạn phải hoàn thành checklist trước khi di chuyển</p>
          </div>

          <TaiXeChecklist
            v-if="xeHienTai?.id"
            :xe-id="xeHienTai.id"
            @checklist-success="handleChecklistSuccess"
            @checklist-critical-error="handleChecklistError"
          />
        </div>

        <!-- ON DUTY -->
        <div v-else-if="step === STEPS.ON_DUTY">
          <div class="safe-box">
            <i class="fa fa-shield"></i> Đang làm việc. Chúc bác tài an toàn!
          </div>

          <!-- ✅ Cẩu hàng nằm ngay HOME (yêu cầu của bạn) -->
          <!-- <div v-if="xeHienTai?.tenLoaiXe === 'Xe Cẩu'" class="section-card">
            <div class="section-title">
              <i class="fa fa-magnet"></i> Cẩu hàng
            </div>
            <div class="section-pad">
              <TaiXeCauHangCard
                v-if="xeHienTai?.id && taiXeHienTai"
                :xe-id="xeHienTai.id"
                :tai-xe="taiXeHienTai"
                :xe-bien-so="xeHienTai.bienSo"
              />
            </div>
          </div> -->

          <!-- ✅ Action cũng nằm ở HOME -->
          <div class="section-card">
            <div class="section-title">
              <i class="fa fa-bolt"></i> Tác vụ nhanh
            </div>
            <div class="action-grid">
              <div class="action-card">
                <SosComponent @report-success="handleSosSuccessFromHome" />
              </div>
              <div class="action-card">
                <TaiXeDoXang
                  v-if="xeHienTai?.id"
                  :xeId="xeHienTai.id"
                  @submit-success="handleXangDauSuccessFromHome"
                />
              </div>
            </div>

            <div class="section-pad pt-0">
              <button class="btn-soft" @click="activeBottomTab = 'ACTIONS'">
                <i class="fa fa-th-large mr-2"></i> Mở tất cả tác vụ
              </button>
            </div>
          </div>

          <button class="btn-end-shift" @click="handleTraXeClick">
            <i class="fa fa-sign-out"></i> Kết thúc ca & Trả xe
          </button>
        </div>
      </div>

      <!-- ===== ACTIONS TAB ===== -->
      <div v-else-if="activeBottomTab === 'ACTIONS'">
        <div v-if="step !== STEPS.ON_DUTY" class="section-card">
          <div class="section-pad text-center text-muted">
            <i class="fa fa-lock fa-2x mb-2"></i>
            <p class="mb-2">
              Bạn phải <b>Nhận xe</b> ở Trang chủ trước khi sử dụng các tác vụ này.
            </p>
            <button class="btn-primary-large" style="max-width:260px" @click="activeBottomTab='HOME'">
              Về Trang chủ
            </button>
          </div>
        </div>

        <div v-else>
          <!-- Grid menu giữ lại -->
          <div class="section-card">
            <div class="section-title">
              <i class="fa fa-th-large"></i> Tác vụ
            </div>

            <div class="action-grid-menu">
              <div
                class="grid-item"
                v-if="xeHienTai?.tenLoaiXe === 'Xe Cẩu'"
                :class="{ active: activeFeature === 'crane' }"
                @click="toggleFeature('crane')"
              >
                <div class="grid-icon color-blue"><i class="fa fa-magnet"></i></div>
                <span class="grid-label">Cẩu hàng</span>
              </div>

              <div
                class="grid-item"
                :class="{ active: activeFeature === 'fuel' }"
                @click="toggleFeature('fuel')"
              >
                <div class="grid-icon color-orange"><i class="fa fa-tint"></i></div>
                <span class="grid-label">Nhiên liệu</span>
              </div>

              <div
                class="grid-item"
                :class="{ active: activeFeature === 'delivery' }"
                @click="toggleFeature('delivery')"
              >
                <div class="grid-icon color-green"><i class="fa fa-cube"></i></div>
                <span class="grid-label">Giao hàng</span>
              </div>

              <div class="grid-item" @click="activeBottomTab = 'MAINTENANCE'">
                <div class="grid-icon color-grey"><i class="fa fa-wrench"></i></div>
                <span class="grid-label">Bảo dưỡng</span>
              </div>

              <div class="grid-item danger-item" @click="showSosModal = true">
                <div class="grid-icon color-red"><i class="fa fa-life-ring"></i></div>
                <span class="grid-label">Báo Sự Cố</span>
              </div>

              <div class="grid-item" @click="handleTraXeClick">
                <div class="grid-icon color-dark"><i class="fa fa-sign-out"></i></div>
                <span class="grid-label">Trả xe</span>
              </div>
            </div>
          </div>

          <!-- expand feature -->
          <div v-if="activeFeature" class="feature-expand-area">
            <div class="feature-header">
              <h3 class="feature-title">{{ featureTitle }}</h3>
              <button class="btn-close-feature" @click="activeFeature = null">
                <i class="fa fa-times"></i> Đóng
              </button>
            </div>

            <div class="feature-body">
              <TaiXeCauHangCard
                v-if="activeFeature === 'crane'"
                :xe-id="xeHienTai.id"
                :tai-xe="taiXeHienTai"
                :xe-bien-so="xeHienTai.bienSo"
              />

              <TaiXeDoXang
                v-if="activeFeature === 'fuel'"
                :xeId="xeHienTai.id"
                @submit-success="handleXangDauSuccess"
              />

              <TaiXeGiaoNhanHang
                v-if="activeFeature === 'delivery'"
                :xe-id="xeHienTai.id"
                :tai-xe-id="taiXeHienTai?.idNguoiDung || taiXeHienTai?.id"
                :assignment-id="currentAssignment?.id"
                :xe-bien-so="xeHienTai.bienSo"
                :tai-xe-ten="taiXeHienTai?.hoTen"
                :demo-only="true"
              />
            </div>
          </div>

          <!-- SOS modal -->
          <div v-if="showSosModal" class="modal-backdrop" @click.self="showSosModal=false">
            <div class="classic-dialog">
              <div class="dialog-header">
                <h3><i class="fa fa-life-ring text-danger"></i> Khẩn cấp SOS</h3>
              </div>
              <div class="dialog-body">
                <SosComponent @report-success="handleSosSuccess" />
              </div>
              <div class="dialog-footer">
                <button class="btn-soft w-100" @click="showSosModal=false">Đóng</button>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- ===== DOCS TAB ===== -->
      <div v-else-if="activeBottomTab === 'DOCS'">
        <div class="section-card">
          <div class="section-title"><i class="fa fa-id-card"></i> Giấy tờ</div>

          <div class="section-pad">
            <div class="doc-section">
              <h4><i class="fa fa-car"></i> Giấy tờ xe</h4>
              <div v-if="!giayToXe.length" class="empty-state">Không có dữ liệu</div>
              <div
                v-for="gt in giayToXe"
                :key="'xe-' + gt.id"
                class="doc-item"
                @click="viewFile(gt.fileUrl)"
              >
                <div class="doc-info">
                  <strong>{{ gt.tenHienThi || formatLoaiGiayTo(gt.loaiGiayTo) }}</strong>
                  <small :class="{ 'text-danger': isExpired(gt.ngayHetHan) }">
                    Hạn: {{ formatDate(gt.ngayHetHan) }}
                  </small>
                </div>
                <i class="fa fa-chevron-right" v-if="gt.fileUrl"></i>
              </div>
            </div>

            <div class="doc-section mt-3">
              <h4><i class="fa fa-user"></i> Giấy tờ cá nhân</h4>
              <div v-if="!giayToTaiXe.length" class="empty-state">Không có dữ liệu</div>
              <div
                v-for="gt in giayToTaiXe"
                :key="'tx-' + gt.id"
                class="doc-item"
                @click="viewFile(gt.fileUrl)"
              >
                <div class="doc-info">
                  <strong>{{ gt.tenHienThi || formatLoaiGiayTo(gt.loaiGiayTo) }}</strong>
                </div>
                <i class="fa fa-chevron-right" v-if="gt.fileUrl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== MAINTENANCE TAB ===== -->
      <div v-else-if="activeBottomTab === 'MAINTENANCE'">
        <div class="section-card">
          <div class="section-title"><i class="fa fa-wrench"></i> Bảo dưỡng</div>
          <div class="section-pad">
            <TaiXeBaoDuongPage />
          </div>
        </div>
      </div>

      <!-- ===== HISTORY TAB ===== -->
      <div v-else-if="activeBottomTab === 'HISTORY'">
        <div class="section-card">
          <div class="section-title"><i class="fa fa-clock-o"></i> Lịch sử</div>
          <div class="section-pad">
            <div class="sub-title">Lịch sử nhận xe</div>
            <LichSuGiaoNhan :phanCongId="currentAssignment?.id" />

            <div class="sub-title mt-3">Lịch sử giao nhận hàng</div>
            <LichSuGiaoNhanHang
              v-if="taiXeHienTai?.idNguoiDung || taiXeHienTai?.id"
              :tai-xe-id="taiXeHienTai?.idNguoiDung || taiXeHienTai?.id"
            />

            <div class="sub-title mt-3">Lịch sử đổ nhiên liệu</div>
            <TaiXeLichSuXangDau
              v-if="xeHienTai?.id"
              :xe-id="xeHienTai.id"
              :tai-xe-id="taiXeHienTai?.idNguoiDung || taiXeHienTai?.id"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- CHECKOUT SHEET -->
    <div v-if="step === STEPS.CHECK_OUT" class="modal-overlay" @click.self="cancelCheckout">
      <div class="modal-container checkout-sheet">
        <div class="modal-header">
          <div class="d-flex align-items-center">
            <i class="fa fa-sign-out text-danger" style="font-size:18px;"></i>
            <div class="ml-2">
              <div class="header-title">Trả xe kết thúc ca</div>
              <div class="header-subtitle">
                {{ xeHienTai?.bienSo || "Xe ?" }}
                <span v-if="currentOdo" class="dot-sep">•</span>
                <span v-if="currentOdo">{{ currentOdo }} km</span>
              </div>
            </div>
          </div>

          <button class="btn-close" @click="cancelCheckout" :disabled="isLoading">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <form class="modal-body checkout-body" @submit.prevent="submitTraXe">
          <div v-if="error" class="alert alert-danger py-2 px-3 mb-3">
            <i class="fa fa-exclamation-triangle mr-2"></i>{{ error }}
          </div>

          <div class="checkout-block">
            <div class="checkout-label">ODO khi trả xe <span class="text-danger">*</span></div>
            <div class="d-flex align-items-center">
              <input
                v-model.number="formTraXe.odo"
                class="checkout-input flex-grow-1"
                type="number"
                inputmode="numeric"
                placeholder="Nhập km hiện tại..."
                min="0"
                required
              />
              <span class="ml-2 unit-label">km</span>
            </div>
          </div>

          <div class="checkout-block">
            <div class="checkout-label">
              Nhiên liệu hiện tại <span class="text-muted small">(không bắt buộc)</span>
            </div>

            <div class="fuel-wrap">
              <input
                v-model.number="formTraXe.mucNhienLieu"
                class="checkout-input"
                type="number"
                inputmode="numeric"
                min="0"
                placeholder="% hoặc lít"
              />

              <div class="fuel-quick-row">
                <button type="button" class="fuel-quick-btn" @click="formTraXe.mucNhienLieu = 25">1/4</button>
                <button type="button" class="fuel-quick-btn" @click="formTraXe.mucNhienLieu = 50">1/2</button>
                <button type="button" class="fuel-quick-btn" @click="formTraXe.mucNhienLieu = 75">3/4</button>
                <button type="button" class="fuel-quick-btn" @click="formTraXe.mucNhienLieu = 100">Đầy</button>
              </div>
            </div>
          </div>

          <div class="checkout-block">
            <div class="checkout-label">
              Ghi chú thêm <span class="text-muted small">(hư hỏng, va quẹt...)</span>
            </div>
            <textarea
              v-model.trim="formTraXe.ghiChu"
              class="checkout-textarea"
              rows="3"
              placeholder="VD: thắng kêu, lốp sau mềm..."
            ></textarea>
          </div>

          <div class="checkout-footer">
            <button type="button" class="btn-cancel" @click="cancelCheckout" :disabled="isLoading">
              Hủy
            </button>

            <button type="submit" class="btn-confirm" :disabled="isLoading || !formTraXe.odo">
              <span v-if="isLoading" class="spinner-border spinner-border-sm mr-2"></span>
              <i v-else class="fa fa-check-circle mr-2"></i>
              Xác nhận trả xe
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- BOTTOM NAV -->
    <nav v-if="!isLoading && step !== STEPS.ERROR" class="bottom-nav">
      <button class="bn-item" :class="{active: activeBottomTab==='HOME'}" @click="activeBottomTab='HOME'">
        <i class="fa fa-home"></i><span>Trang chủ</span>
      </button>
      <button class="bn-item" :class="{active: activeBottomTab==='ACTIONS'}" @click="activeBottomTab='ACTIONS'">
        <i class="fa fa-bolt"></i><span>Tác vụ</span>
      </button>
      <button class="bn-item" :class="{active: activeBottomTab==='DOCS'}" @click="activeBottomTab='DOCS'">
        <i class="fa fa-id-card"></i><span>Giấy tờ</span>
      </button>
      <button class="bn-item" :class="{active: activeBottomTab==='MAINTENANCE'}" @click="activeBottomTab='MAINTENANCE'">
        <i class="fa fa-wrench"></i><span>Bảo dưỡng</span>
      </button>
      <button class="bn-item" :class="{active: activeBottomTab==='HISTORY'}" @click="activeBottomTab='HISTORY'">
        <i class="fa fa-history"></i><span>Lịch sử</span>
      </button>
    </nav>
  </div>
</template>
<script>
import { mapState, mapActions, mapGetters } from "pinia";
import { useDriverDashboardStore } from "@/stores/driverDashboardStore";

import SosComponent from "./SosComponent.vue";
import TaiXeDoXang from "./TaiXeDoXang.vue";
import TaiXeChecklist from "./TaiXeChecklist.vue";
import TaiXeVanHang from "./TaiXeVanHang.vue";
import LichSuGiaoNhan from "./LichSuGiaoNhan.vue";
import TaiXeCauHangCard from "./TaiXeCauHangCard.vue";
import TaiXeLichSuXangDau from "./TaiXeLichSuXangDau.vue";
import TaiXeBaoDuongPage from "@/views/TaiXeBaoDuong/TaiXeBaoDuongPage.vue";
import TaiXeGiaoNhanHang from "./TaiXeGiaoNhanHang.vue";
import LichSuGiaoNhanHang from "@/views/LichSuGiaoNhanHang.vue";

function formatDate(dateString) {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (e) {
    return "-";
  }
}

export default {
  name: "TaiXe",
  components: {
    SosComponent,
    TaiXeDoXang,
    TaiXeChecklist,
    TaiXeVanHang,
    LichSuGiaoNhan,
    TaiXeCauHangCard,
    TaiXeLichSuXangDau,
    TaiXeBaoDuongPage,
    TaiXeGiaoNhanHang,
    LichSuGiaoNhanHang,
  },
  data() {
    return {
      activeBottomTab: "HOME",
      activeFeature: null,
      showSosModal: false,
      formTraXe: { odo: null, mucNhienLieu: null, ghiChu: "" },
    };
  },
  computed: {
    ...mapState(useDriverDashboardStore, ["step", "error", "hoSo", "currentAssignment", "currentOdo"]),
    ...mapGetters(useDriverDashboardStore, ["STEPS", "xeHienTai", "taiXeHienTai", "giayToXe", "giayToTaiXe"]),

    isLoading() {
      return this.step === (this.STEPS ? this.STEPS.LOADING : 0);
    },

    featureTitle() {
      if (this.activeFeature === "crane") return "Vận hành Cẩu";
      if (this.activeFeature === "fuel") return "Cập nhật nhiên liệu";
      if (this.activeFeature === "delivery") return "Giao hàng";
      return "Chi tiết";
    },

    xeIcon() {
      const t = (this.xeHienTai?.tenLoaiXe || "").toLowerCase();
      if (t.includes("cẩu")) return "fa fa-industry";
      if (t.includes("tải")) return "fa fa-truck";
      return "fa fa-car";
    },
  },
  methods: {
    ...mapActions(useDriverDashboardStore, [
      "initializeDashboard",
      "nhanXe",
      "handleChecklistSuccess",
      "openCheckoutModal",
      "cancelCheckout",
      "traXe",
      "clearHoSo",
    ]),

    formatDate,

    toggleFeature(featureName) {
      this.activeFeature = this.activeFeature === featureName ? null : featureName;
    },

    handleSosSuccess() {
      this.showSosModal = false;
      alert("Đã gửi yêu cầu SOS thành công.");
    },

    // ✅ HOME dùng chung logic, chỉ khác UX (không mở modal)
    handleSosSuccessFromHome() {
      alert("Đã gửi yêu cầu SOS thành công.");
      this.activeBottomTab = "MAINTENANCE";
    },

    handleXangDauSuccess() {
      this.activeFeature = null;
      this.initializeDashboard();
      alert("Cập nhật nhiên liệu thành công.");
    },

    handleXangDauSuccessFromHome() {
      this.initializeDashboard();
      alert("Cập nhật nhiên liệu thành công.");
      this.activeBottomTab = "HISTORY";
    },

    handleChecklistError() {
      alert("Checklist có lỗi nghiêm trọng! Vui lòng kiểm tra xe.");
      this.activeBottomTab = "MAINTENANCE";
    },

    async handleNhanXe() {
      try {
        await this.nhanXe();
      } catch (e) {
        console.error("Lỗi nhận xe:", e);
      }
    },

    handleTraXeClick() {
      this.formTraXe.odo = this.currentOdo ?? this.xeHienTai?.odoGanNhat ?? null;
      this.formTraXe.mucNhienLieu = null;
      this.formTraXe.ghiChu = "";
      this.openCheckoutModal();
    },

    async submitTraXe() {
      const odo = this.formTraXe.odo;
      const hasOdo = odo !== null && odo !== undefined && String(odo).trim() !== "";
      if (hasOdo) {
        const odoNum = Number(odo);
        if (!Number.isFinite(odoNum) || odoNum <= 0) {
          alert("Nếu nhập ODO thì phải là số dương.");
          return;
        }
        this.formTraXe.odo = odoNum;
      } else {
        this.formTraXe.odo = null;
      }

      try {
        await this.traXe(this.formTraXe);
        alert("Đã trả xe thành công.");
        this.activeBottomTab = "HOME";
      } catch (e) {
        console.error(e);
      }
    },

    viewFile(url) {
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else alert("Không có file đính kèm.");
    },

    isExpired(dateString) {
      if (!dateString) return false;
      try {
        const d = new Date(dateString);
        const today = new Date();
        d.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return d < today;
      } catch (e) {
        return false;
      }
    },

    formatLoaiGiayTo(loai) {
      return loai || "Giấy tờ";
    },
  },
  mounted() {
    this.initializeDashboard();
  },
};
</script>
<style scoped>
* { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }

.mobile-driver-wrapper{
  min-height:100vh;
  background:#f5f7fa;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  padding-bottom: 88px;
}

.main-content{ padding:16px; }

.loading-screen,.error-screen,.empty-screen{
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  padding:60px 20px; text-align:center;
}
.spinner{
  width:48px; height:48px;
  border:4px solid #e0e0e0; border-top-color:#667eea;
  border-radius:50%; animation:spin .8s linear infinite;
}
@keyframes spin{ to{ transform:rotate(360deg);} }

.error-screen i,.empty-screen i{ font-size:64px; margin-bottom:16px; color:#e53e3e; }
.empty-screen i{ color:#a0aec0; }
.error-screen h3,.empty-screen h3{ margin:0 0 8px 0; color:#2d3748; }
.error-screen p,.empty-screen p,.loading-screen p{ color:#718096; margin:0 0 24px 0; }

.vehicle-card{
  background:#fff; border-radius:16px; padding:16px; margin-bottom:12px;
  box-shadow:0 2px 8px rgba(0,0,0,.08);
}
.vehicle-header{ display:flex; justify-content:space-between; align-items:center; gap:10px; }
.vh-left{ display:flex; align-items:center; gap:8px; }
.vehicle-header i{ color:#667eea; font-size:18px; }
.vehicle-header strong{ font-size:20px; color:#2d3748; }
.odo-badge{
  background:#edf2f7; padding:6px 12px; border-radius:20px;
  font-size:13px; color:#4a5568; display:flex; align-items:center; gap:6px;
  white-space:nowrap;
}
.vehicle-info{ color:#718096; font-size:14px; margin-top:6px; }
.pill-row{ display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }

.pill{
  background:#f3f4f6;
  border:1px solid #e5e7eb;
  color:#374151;
  font-size:11px;
  font-weight:700;
  padding:4px 8px;
  border-radius:999px;
}

.driver-mini{
  background:#fff; border-radius:16px; padding:12px; margin-bottom:16px;
  box-shadow:0 2px 8px rgba(0,0,0,.08);
  display:flex; align-items:center; justify-content:space-between; gap:10px;
}
.d-left{ display:flex; align-items:center; gap:10px; min-width:0; }
.avatar{
  width:44px; height:44px; border-radius:999px; object-fit:cover;
  border:1px solid #e5e7eb; background:#f3f4f6;
}
.d-text{ min-width:0; }
.name{ font-weight:800; color:#111827; font-size:14px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sub{ display:flex; flex-wrap:wrap; gap:6px; margin-top:6px; }
.d-right{ display:flex; gap:8px; }
.quick{
  width:42px; height:42px; border-radius:12px;
  background:#111827; color:#fff;
  display:flex; align-items:center; justify-content:center;
  text-decoration:none;
}
.quick:active{ transform:scale(.98); }

.step-container{
  background:#fff; border-radius:16px; padding:24px; margin-bottom:16px;
  box-shadow:0 2px 8px rgba(0,0,0,.08);
}
.step-container.warning{ border:2px solid #f6ad55; }
.step-header{ display:flex; align-items:center; gap:12px; margin-bottom:16px; }
.step-number{
  width:40px; height:40px; border-radius:50%;
  background:#edf2f7; color:#4a5568;
  display:flex; align-items:center; justify-content:center;
  font-weight:800; font-size:18px; flex-shrink:0;
}
.step-header h2{ margin:0; font-size:20px; color:#2d3748; font-weight:700; }
.step-desc{ color:#718096; margin:0 0 24px 0; line-height:1.5; }

.warning-box{
  background:#fffbeb; border:1px solid #f6ad55;
  border-radius:12px; padding:16px; margin-bottom:20px;
  display:flex; gap:12px;
}
.warning-box i{ color:#f6ad55; font-size:22px; flex-shrink:0; }
.warning-box p{ margin:0; color:#744210; flex:1; }

.btn-primary-large{
  width:100%;
  background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);
  color:#fff; border:none; border-radius:12px;
  padding:16px; font-size:18px; font-weight:700;
  display:flex; align-items:center; justify-content:center; gap:8px;
  cursor:pointer; transition:all .2s;
  box-shadow:0 4px 12px rgba(102,126,234,.4);
}
.btn-primary-large:active{ transform:translateY(2px); box-shadow:0 2px 8px rgba(102,126,234,.4); }
.btn-primary-large:disabled{ opacity:.6; cursor:not-allowed; }

.safe-box{
  background:#ecfdf5; color:#065f46;
  padding:12px; border-radius:12px;
  border:1px solid #a7f3d0;
  font-weight:700;
  margin-bottom:12px;
}

.section-card{
  background:#fff; border-radius:16px; overflow:hidden; margin-bottom:16px;
  box-shadow:0 2px 8px rgba(0,0,0,.08);
}
.section-title{
  padding:14px 16px;
  border-bottom:1px solid #e2e8f0;
  font-weight:800; color:#2d3748;
  display:flex; align-items:center; gap:8px;
}
.section-pad{ padding:16px; }
.section-pad.pt-0{ padding-top:0; }

.action-grid{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
  padding:16px;
}
.action-card{
  background:#fff; border-radius:12px; overflow:hidden;
  border:1px solid #eef2f7;
}

.btn-soft{
  width:100%;
  background:#111827;
  color:#fff;
  border:none;
  border-radius:12px;
  padding:12px 14px;
  font-weight:800;
}
.btn-soft:active{ transform:scale(.99); }

.btn-end-shift{
  width:100%;
  background:#e53e3e; color:#fff;
  border:none; border-radius:12px;
  padding:16px; font-size:16px; font-weight:800;
  display:flex; align-items:center; justify-content:center; gap:8px;
  cursor:pointer; margin-top:8px; transition:all .2s;
}
.btn-end-shift:active{ transform:translateY(2px); background:#c53030; }

.empty-state{
  text-align:center; padding:20px 12px; color:#a0aec0;
}

.doc-section h4{
  font-size:15px; color:#4a5568; margin:0 0 12px 0;
  display:flex; align-items:center; gap:8px;
}
.doc-item{
  display:flex; align-items:center; justify-content:space-between;
  padding:14px;
  background:#f7fafc;
  border-radius:10px;
  margin-bottom:8px;
  cursor:pointer;
  transition:all .2s;
}
.doc-item:active{ background:#edf2f7; transform:scale(.99); }
.doc-info strong{ display:block; color:#2d3748; font-size:15px; margin-bottom:4px; }
.doc-info small{ font-size:13px; color:#718096; }
.text-danger{ color:#e53e3e !important; font-weight:800; }

/* actions grid menu (giữ lại) */
.action-grid-menu{
  display:grid;
  grid-template-columns: repeat(3, 1fr);
  gap:12px;
  padding:16px;
}
.grid-item{
  background:#fff;
  border:1px solid #d1d5db;
  border-radius:14px;
  height:96px;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  cursor:pointer;
  transition:all .1s;
}
.grid-item:active{ transform:scale(.97); background:#f9fafb; }
.grid-item.active{ border-color:#2563eb; background:#eff6ff; }
.grid-item.danger-item{ border-color:#fee2e2; }

.grid-icon{
  width:40px; height:40px; border-radius:50%;
  display:flex; align-items:center; justify-content:center;
  font-size:18px; margin-bottom:8px;
}
.grid-label{ font-size:12px; font-weight:800; color:#374151; }

.color-blue { background:#dbeafe; color:#2563eb; }
.color-orange { background:#ffedd5; color:#ea580c; }
.color-green { background:#dcfce7; color:#16a34a; }
.color-grey { background:#f3f4f6; color:#4b5563; }
.color-red { background:#fee2e2; color:#dc2626; }
.color-dark { background:#e5e7eb; color:#1f2937; }

.feature-expand-area{
  background:#fff;
  border:1px solid #d1d5db;
  border-radius:16px;
  overflow:hidden;
  margin-top:12px;
  box-shadow:0 4px 10px rgba(0,0,0,.08);
}
.feature-header{
  background:#f9fafb;
  padding:12px 16px;
  border-bottom:1px solid #e5e7eb;
  display:flex;
  justify-content:space-between;
  align-items:center;
}
.feature-title{ font-size:15px; font-weight:900; margin:0; }
.btn-close-feature{
  background:none; border:none; color:#dc2626;
  font-size:13px; font-weight:800; cursor:pointer;
}
.feature-body{ padding:16px; }

/* modal + checkout (giữ kiểu bạn đang dùng ở UI mới) */
.modal-backdrop{
  position:fixed; inset:0;
  background:rgba(0,0,0,.6);
  display:flex; align-items:center; justify-content:center;
  z-index:1000;
}
.classic-dialog{
  background:#fff; width:85%; max-width:340px;
  border-radius:12px; overflow:hidden;
  box-shadow:0 10px 15px rgba(0,0,0,.12);
}
.dialog-header{ padding:16px; border-bottom:1px solid #f3f4f6; text-align:center; }
.dialog-body{ padding:20px; }
.dialog-footer{ padding:12px; background:#f9fafb; }

.modal-overlay{
  position:fixed; inset:0;
  background:rgba(0,0,0,.6);
  display:flex; align-items:flex-end;
  z-index:1000;
}
.modal-container{
  background:#fff; width:100%;
  border-radius:24px 24px 0 0;
  max-height:90vh; overflow:hidden;
}
.modal-header{
  padding:20px;
  border-bottom:1px solid #e2e8f0;
  display:flex; align-items:center; justify-content:space-between;
}
.btn-close{ background:transparent; border:none; color:#4a5568; font-size:18px; padding:6px 8px; }
.btn-close:disabled{ opacity:.4; }

.checkout-sheet{ display:flex; flex-direction:column; }
.header-title{ font-size:16px; font-weight:800; color:#2d3748; }
.header-subtitle{ font-size:13px; color:#718096; display:flex; flex-wrap:wrap; gap:4px; }
.dot-sep{ color:#cbd5e0; }

.checkout-body{
  flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch;
  background:#f9fafb; padding:16px;
}
.checkout-block{
  background:#fff; border-radius:16px;
  padding:12px 14px; margin-bottom:12px;
  border:1px solid #eee;
}
.checkout-label{ font-size:14px; font-weight:800; color:#2d3748; margin-bottom:8px; display:flex; gap:6px; flex-wrap:wrap; }
.checkout-input{
  width:100%;
  border:1px solid #cbd5e0;
  border-radius:10px;
  background:#fff;
  padding:12px;
  font-size:16px;
  color:#2d3748;
}
.unit-label{ font-size:14px; color:#4a5568; min-width:32px; text-align:right; }
.fuel-wrap{ display:flex; flex-direction:column; gap:10px; }
.fuel-quick-row{ display:flex; flex-wrap:wrap; gap:6px; }
.fuel-quick-btn{
  flex:1 1 calc(25% - 6px);
  background:#f7fafc;
  border:1px solid #e2e8f0;
  border-radius:10px;
  padding:10px 0;
  font-size:13px;
  font-weight:800;
  color:#2d3748;
}
.checkout-textarea{
  width:100%;
  border:1px solid #cbd5e0;
  border-radius:10px;
  background:#fff;
  padding:12px;
  font-size:15px;
  color:#2d3748;
  min-height:70px;
  resize:none;
}
.checkout-footer{
  position:sticky; bottom:0; left:0; right:0;
  background:#f9fafb;
  display:flex; gap:10px;
  padding-top:8px; margin-top:8px;
}
.btn-cancel,.btn-confirm{
  flex:1; border:none; border-radius:12px;
  font-size:16px; font-weight:800;
  padding:14px 12px;
  display:flex; align-items:center; justify-content:center;
}
.btn-cancel{ background:#e2e8f0; color:#2d3748; }
.btn-confirm{ background:#e53e3e; color:#fff; }
.btn-confirm:disabled,.btn-cancel:disabled{ opacity:.6; }

.spinner-border{
  width:1rem; height:1rem;
  border:2px solid rgba(255,255,255,.4);
  border-top-color:#fff;
  border-radius:50%;
  animation:spin .6s linear infinite;
}

/* bottom nav */
.bottom-nav{
  position:fixed; left:0; right:0; bottom:0;
  height:72px;
  background:rgba(255,255,255,.95);
  border-top:1px solid #e2e8f0;
  display:flex;
  z-index:999;
  backdrop-filter: blur(10px);
}
.bn-item{
  flex:1; background:none; border:none;
  padding:10px 4px 8px;
  display:flex; flex-direction:column;
  align-items:center; justify-content:center;
  gap:6px;
  color:#718096; font-size:12px;
}
.bn-item i{ font-size:18px; }
.bn-item.active{ color:#667eea; font-weight:900; }
.bn-item:active{ transform:scale(.98); }

.sub-title{
  font-size:13px;
  font-weight:900;
  color:#6b7280;
  text-transform: uppercase;
  margin: 6px 0 10px;
}
.mt-3{ margin-top: 12px; }
.text-center{ text-align:center; }
.text-muted{ color:#718096; }
.mb-2{ margin-bottom: 8px; }
</style>
